/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { DeferredPromise } from '../../../../../../base/common/async.js';
import { IMarkdownString } from '../../../../../../base/common/htmlContent.js';
import { IChatQuestion, IChatQuestionAnswers, IChatQuestionCarousel } from '../../chatService/chatService.js';
import { ToolDataSource } from '../../tools/languageModelToolsService.js';

/**
 * Runtime representation of a question carousel with a {@link DeferredPromise}
 * that is resolved when the user submits answers. {@link toJSON} strips the
 * completion so only serialisable data is persisted.
 */
export class ChatQuestionCarouselData implements IChatQuestionCarousel {
	public readonly kind = 'questionCarousel' as const;
	public readonly completion = new DeferredPromise<{ answers: IChatQuestionAnswers | undefined }>();
	public draftAnswers: IChatQuestionAnswers | undefined;
	public draftCurrentIndex: number | undefined;
	public draftCollapsed: boolean | undefined;
	/**
	 * Set to `true` when the carousel was dismissed because the user typed
	 * directly in the associated terminal instead of using the carousel UI.
	 */
	public dismissedByTerminalInput?: boolean;

	/**
	 * True when the input was accepted/answered outside the carousel UI (e.g.
	 * via voice) without structured answers, so the summary reads "Answered".
	 */
	public answeredExternally?: boolean;

	/**
	 * Marks the carousel as dismissed with the given answers and clears draft
	 * state. Safe to call multiple times — subsequent calls are no-ops.
	 */
	dismiss(answers: IChatQuestionAnswers | undefined): void {
		if (this.isUsed) {
			return;
		}
		this.data = answers ?? {};
		this.isUsed = true;
		this.draftAnswers = undefined;
		this.draftCurrentIndex = undefined;
		this.draftCollapsed = undefined;
		void this.completion.complete({ answers });
	}

	constructor(
		public questions: IChatQuestion[],
		public allowSkip: boolean,
		public resolveId?: string,
		public data?: IChatQuestionAnswers,
		public isUsed?: boolean,
		public message?: string | IMarkdownString,
		public source?: ToolDataSource,
		public terminalId?: string,
	) { }

	toJSON(): IChatQuestionCarousel {
		return {
			kind: this.kind,
			questions: this.questions,
			allowSkip: this.allowSkip,
			resolveId: this.resolveId,
			data: this.data,
			isUsed: this.isUsed,
			answeredExternally: this.answeredExternally,
			message: this.message,
			source: this.source,
			terminalId: this.terminalId,
		};
	}
}
